import { Input, Output, Component, OnInit, AfterViewInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { GridOptions } from 'ag-grid';

import { PropertyDetail, PropertyMetric } from '~/core/model';

import { SIZES } from '~/shared/configs';
import { COLUMN_TYPE } from '~shared/components/grids';
import { NumericPipe } from '~shared/pipes';
import { PropertyImageCellComponent, PropertyNameRendererComponent } from '../grids';
import { GridHeaderComponent } from '~shared/components/assorted/grid-header.component';
import { AppConstants } from '~/shared/utils/app-constants';

@Component({
  selector: 'idap-property-table',
  templateUrl: './property-table.component.html',
  styleUrls: ['./property-table.component.scss']
})
export class PropertyTableComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() properties: Observable<PropertyMetric[]>;
  @Input() property: PropertyDetail;
  @Output() onPropertySelected: EventEmitter<PropertyDetail> = new EventEmitter();

  propertyDataReady: Subject<boolean> = new Subject();
  onDestroy: Subject<PropertyTableComponent> = new Subject();

  private renderCurrencyValue = function(params) {
    return `<span class="text-center">${new NumericPipe().transform(params.value, COLUMN_TYPE.CURRENCY)}</span>`;
  }

  private renderRPSFCurrencyValue = function(params) {
    return `<span class="text-center">${new NumericPipe().transform(params.value, COLUMN_TYPE.CURRENCY, 3)}</span>`;
  }

  private renderPercentageValue = function(params) {
    return `<span class="text-center">${new NumericPipe().transform(params.value, COLUMN_TYPE.PERCENTAGE, 1)}</span>`;
  }

  options: GridOptions = {
    rowHeight: SIZES.characterHeight(3),
    headerHeight: SIZES.characterHeight(3),
    rowClass: 'table-row-flex',
    rowSelection: 'single',
    getRowNodeId: (data) => data.id,
    columnDefs: [
      { colId: 'image', headerName: ' ', cellClass: 'table-cell-center', field: 'id', cellRendererFramework: PropertyImageCellComponent, width: SIZES.characterWidth(7) },
      { colId: 'name', headerName: 'Name', field: 'propertyName', tooltipField: 'propertyName', cellRendererFramework: PropertyNameRendererComponent, width: SIZES.characterWidth(27), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, headerClass: 'align-left', cellClass: 'table-cell-ellipsis text-blue' },
      { colId: 'occupancy', headerName: 'Occupancy', field: 'metrics.occupancy.metricValue', width: SIZES.characterWidth(12), cellRenderer: this.renderPercentageValue, headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'effectiveRent', headerName: 'Effective Rent', field: 'metrics.effectiveRent.metricValue', width: SIZES.characterWidth(9), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellRenderer: this.renderCurrencyValue, cellClass: 'table-cell-center' },
      { colId: 'effectiveRentPerSquareFoot', headerName: 'Effective RPSF', field: 'metrics.effectiveRentPerSquareFoot.metricValue', width: SIZES.characterWidth(9), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellRenderer: this.renderRPSFCurrencyValue, cellClass: 'table-cell-center' },
      { colId: 'annualRentChange', headerName: 'Annual Rent Change', field: 'metrics.annualRentChange.metricValue', cellRenderer: this.renderPercentageValue, width: SIZES.characterWidth(11.5), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'askingRent', headerName: 'Asking Rent', field: 'metrics.askingRent.metricValue', width: SIZES.characterWidth(13), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellRenderer: this.renderCurrencyValue, cellClass: 'table-cell-center' },
      { colId: 'concessions', headerName: 'Concessions', field: 'metrics.concessions.metricValueFormatted', width: SIZES.characterWidth(12), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'unitCount', headerName: 'Units', field: 'unitcount', width: SIZES.characterWidth(8.5), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'averageSquareFootage', headerName: 'Avg Sq Ft', field: 'averageSquareFootage', width: SIZES.characterWidth(9.5), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'class', headerName: 'Class', field: 'class', width: SIZES.characterWidth(8.5), cellRenderer: params => {
        if(params.data.type === AppConstants.TYPES.STUDENT_HOUSING) {
          return 'N/A';
        } else {
          return params.value;
        }
      }, headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'yearBuilt', headerName: 'Age', field: 'yearBuilt', width: SIZES.characterWidth(8), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'stories', headerName: 'Stories', field: 'stories', width: SIZES.characterWidth(9.5), headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'table-cell-center' },
      { colId: 'city', headerName: 'City', field: 'city', width: SIZES.characterWidth(12), headerClass: 'align-left', cellClass: 'align-left', headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent },
      { colId: 'address', headerName: 'Address', field: 'address', width: SIZES.characterWidth(19), headerClass: 'align-left', headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent, cellClass: 'align-left' },
      { colId: 'state', headerName: 'State', field: 'state', width: SIZES.characterWidth(9), cellClass: 'table-cell-center', headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent },
      { colId: 'zipCode', headerName: 'Zip', field: 'zipCode', width: SIZES.characterWidth(9), cellClass: 'table-cell-center', headerComponentFramework: <{ new(): GridHeaderComponent }>GridHeaderComponent }
    ]
  };
  overlayLoadingTemplate;

  constructor() {
    this.overlayLoadingTemplate = '<div class="raul-content-loader-wrapper">' +
    '<div class="raul-content-loader"></div>'+
    '</div>';
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.properties
      .takeUntil(this.onDestroy)
      .subscribe(data => {
        this.options.api.setRowData(data);
        this.propertyDataReady.next(data.length > 0);
      });
  }

  onCellClicked(e) {
    this.onPropertySelected.next(e.node.data);
  }

  selectRow(id: any): void {
    const row = this.options.api && this.options.api.getRowNode(id);
    if (row) {
      row.setSelected(true, true);
      this.options.api.ensureIndexVisible(row.rowIndex, 'top');
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(this);
    this.onDestroy.complete();
    this.onPropertySelected.complete();
    this.propertyDataReady.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/features/property/components/tables/property-table.component.ts